import { useState, useEffect, useRef, useCallback } from 'react'
import toast from 'react-hot-toast'
import { iotControllerApi, parameterLibraryApi } from '../../api/services'

// ─────────────────────────────────────────────────────────────────────────────
// Inline library dropdown — shows grouped options with name + unit badge
// ─────────────────────────────────────────────────────────────────────────────
const LibrarySelect = ({ value, grouped, onChange, onClear }) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef(null)

  // Close on outside click
  useEffect(() => {
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSelect = item => {
    onChange(item)
    setOpen(false)
    setSearch('')
  }

  const handleClear = e => {
    e.stopPropagation()
    onClear()
    setOpen(false)
    setSearch('')
  }

  // Flat filtered list for rendering
  const filtered = Object.entries(grouped).reduce((acc, [cat, items]) => {
    const hits = items.filter(
      i =>
        i.name.toLowerCase().includes(search.toLowerCase()) ||
        cat.toLowerCase().includes(search.toLowerCase())
    )
    if (hits.length) acc.push({ cat, items: hits })
    return acc
  }, [])

  const buttonLabel = value ? value.name : 'Select…'
  const hasValue = !!value

  return (
    <div ref={ref} className='relative'>
      <button
        type='button'
        onClick={() => setOpen(o => !o)}
        className={`flex items-center justify-between w-full text-[11px] border rounded-lg px-2 py-1.5 text-left truncate transition-colors ${
          hasValue
            ? 'border-blue-300 bg-blue-50 text-blue-800'
            : 'border-gray-200 bg-white text-gray-400 hover:border-gray-300'
        }`}
      >
        <span className='truncate'>{buttonLabel}</span>
        <div className='flex items-center gap-1 flex-shrink-0 ml-1'>

          {hasValue ? (
            <span
              className='text-blue-400 hover:text-red-500 transition-colors'
              onClick={handleClear}
              title='Clear'
            >
              <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
              </svg>
            </span>
          ) : (
            <svg
              className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
            </svg>
          )}
        </div>
      </button>

      {open && (
        <div className='absolute z-50 top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl w-56 flex flex-col max-h-64 overflow-hidden'>
          {/* Search */}
          <div className='px-2 pt-2 pb-1 flex-shrink-0'>
            <input
              autoFocus
              type='text'
              placeholder='Search…'
              value={search}
              onChange={e => setSearch(e.target.value)}
              className='w-full text-[11px] border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100'
            />
          </div>

          {/* Options */}
          <div className='overflow-y-auto flex-1 px-1 pb-1'>
            {filtered.length === 0 ? (
              <p className='text-[11px] text-gray-400 text-center py-3'>No results</p>
            ) : (
              filtered.map(({ cat, items }) => (
                <div key={cat}>
                  <p className='text-[9px] font-bold text-gray-400 uppercase tracking-widest px-2 pt-2 pb-0.5'>
                    {cat}
                  </p>
                  {items.map(item => (
                    <button
                      key={item.id}
                      type='button'
                      onClick={() => handleSelect(item)}
                      className='flex items-center justify-between w-full px-2 py-1.5 rounded-lg hover:bg-blue-50 transition-colors text-left'
                    >
                      <span className='text-[11px] text-gray-700 font-medium'>{item.name}</span>
                      {item.unit && (
                        <span className='text-[9px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded'>
                          {item.unit}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main modal
// ─────────────────────────────────────────────────────────────────────────────
const ParamMappingModal = ({ controller, tenantId, livePayload, onClose }) => {
  // rows shape: { param_key, library_id, libraryItem, display_name, unit }
  const [rows, setRows] = useState([])
  const [libGrouped, setLibGrouped] = useState({})

  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const liveValues = livePayload?.payload?.Data || livePayload?.payload || {}
  const liveKeys = Object.keys(liveValues).filter(k => /^p\d+$/i.test(k))

  // ── Load library + existing mappings in parallel ──────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const [libRes, paramsRes] = await Promise.all([
          parameterLibraryApi.getAll(),
          iotControllerApi.getParams(controller.id, tenantId)
        ])

        const libData  = libRes.data?.data || {}
        const grouped  = libData.grouped || {}
        const allItems = libData.items   || []
        const byId     = Object.fromEntries(allItems.map(i => [i.id, i]))

        setLibGrouped(grouped)

        const existing = paramsRes.data?.data || []
        const mappedByKey = Object.fromEntries(existing.map(m => [m.param_key, m]))

        const savedKeys = existing.map(m => m.param_key)
        const allKeys   = [...new Set([...liveKeys, ...savedKeys])]
        const keysToShow =
          allKeys.length > 0
            ? allKeys
            : Array.from({ length: 12 }, (_, i) => `p${i + 1}`)

        setRows(
          keysToShow.map(k => {
            const m = mappedByKey[k]
            return {
              param_key:   k,
              library_id:  m?.library_id   ?? null,
              libraryItem: m?.library_id ? byId[m.library_id] ?? null : null,
              display_name: m?.display_name ?? '',
              unit:         m?.unit         ?? '',
              min_value:    m?.min_value    ?? '',
              max_value:    m?.max_value    ?? '',
            }
          })
        )
      } catch {
        toast.error('Failed to load parameters')
      } finally {
        setIsLoading(false)
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller.id, tenantId])

  // ── Field change helpers ──────────────────────────────────────────────────
  const handleField = useCallback((index, field, value) => {
    setRows(prev =>
      prev.map((r, i) => (i === index ? { ...r, [field]: value } : r))
    )
  }, [])

  const handleLibrarySelect = useCallback((index, item) => {
    setRows(prev =>
      prev.map((r, i) => {
        if (i !== index) return r
        // Pick the best unit: keep current if it's in the new item's options, otherwise use the item default
        const opts = item.unit_options || []
        const keepUnit = r.unit && opts.includes(r.unit) ? r.unit : (item.unit || opts[0] || r.unit || '')
        return {
          ...r,
          library_id:   item.id,
          libraryItem:  item,
          display_name: item.name,
          unit:         keepUnit,
        }
      })
    )
  }, [])

  const handleLibraryClear = useCallback(index => {
    setRows(prev =>
      prev.map((r, i) =>
        i === index ? { ...r, library_id: null, libraryItem: null } : r
      )
    )
  }, [])

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const payload = rows
      .filter(r => r.library_id || r.display_name.trim())
      .map(r => ({
        param_key:    r.param_key,
        library_id:   r.library_id   ?? null,
        display_name: r.display_name.trim() || null,
        unit:         r.unit.trim()         || null,
        min_value:    r.min_value !== '' && r.min_value !== null ? Number(r.min_value) : null,
        max_value:    r.max_value !== '' && r.max_value !== null ? Number(r.max_value) : null,
      }))

    setIsSaving(true)
    try {
      await iotControllerApi.saveParams(controller.id, payload, tenantId)
      toast.success('Parameter mappings saved')
      onClose()
    } catch {
      toast.error('Failed to save mappings')
    } finally {
      setIsSaving(false)
    }
  }

  // ── Counts for header badge ───────────────────────────────────────────────
  const detectedCount = liveKeys.length
  const configuredCount = rows.filter(r => r.library_id || r.display_name).length

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4'>
      <div className='bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]'>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className='flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0'>
          <div>
            <h2 className='text-sm font-bold text-gray-800'>Set Parameters</h2>
            <p className='text-[11px] text-gray-400 mt-0.5'>{controller.name}</p>
            <div className='flex items-center gap-2 mt-1'>
              {detectedCount > 0 && (
                <span className='inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full'>
                  <span className='w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse' />
                  {detectedCount} param{detectedCount !== 1 ? 's' : ''} detected
                </span>
              )}
              {configuredCount > 0 && (
                <span className='text-[10px] text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full font-semibold'>
                  {configuredCount} configured
                </span>
              )}
            </div>
          </div>

          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 transition-colors'
          >
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
            </svg>
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <div className='overflow-y-auto flex-1 px-5 py-3'>
          {isLoading ? (
            <div className='flex items-center justify-center py-12 text-gray-400 text-sm'>
              <div className='w-5 h-5 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin mr-2' />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-12 text-gray-400 text-sm'>
              <svg className='w-10 h-10 mb-3 text-gray-200' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5}
                  d='M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0' />
              </svg>
              <p className='font-medium'>No live payload received yet</p>
              <p className='text-xs mt-1 text-gray-300'>Send a message from the device to detect params</p>
            </div>
          ) : (
            <>
              {/* Column headers */}
              <div className='grid grid-cols-[48px_150px_54px_54px_54px_50px] gap-2 mb-2 px-1'>
                <span className='text-[10px] font-semibold text-gray-400 uppercase tracking-wide'>Key</span>
                <span className='text-[10px] font-semibold text-gray-400 uppercase tracking-wide'>Library</span>
                <span className='text-[10px] font-semibold text-gray-400 uppercase tracking-wide'>Unit</span>
                <span className='text-[10px] font-semibold text-gray-400 uppercase tracking-wide'>Min</span>
                <span className='text-[10px] font-semibold text-gray-400 uppercase tracking-wide'>Max</span>
                <span className='text-[10px] font-semibold text-gray-400 uppercase tracking-wide text-right pr-1'>Live</span>
              </div>

              <div className='space-y-1.5'>
                {rows.map((row, i) => {
                  const liveVal = liveValues[row.param_key]
                  const hasLive = liveVal !== undefined && liveVal !== null

                  return (
                    <div
                      key={row.param_key}
                      className={`grid grid-cols-[48px_150px_54px_54px_54px_50px] gap-2 items-center rounded-lg px-1 py-1 ${
                        hasLive ? 'bg-emerald-50/40' : ''
                      }`}
                    >
                      {/* Key badge */}
                      <span
                        className={`text-[11px] font-mono font-bold rounded px-1.5 py-1 text-center border ${
                          hasLive
                            ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                            : 'text-blue-700 bg-blue-50 border-blue-200'
                        }`}
                      >
                        {row.param_key}
                      </span>

                      {/* Library dropdown */}
                      <LibrarySelect
                        value={row.libraryItem}
                        grouped={libGrouped}
                        onChange={item => handleLibrarySelect(i, item)}
                        onClear={() => handleLibraryClear(i)}
                      />

                      {/* Unit — dropdown when library options exist, text input otherwise */}
                      {row.libraryItem?.unit_options?.length > 0 ? (
                        <select
                          value={row.unit}
                          onChange={e => handleField(i, 'unit', e.target.value)}
                          className='text-xs border border-gray-200 rounded-lg px-1.5 py-1.5 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 w-full bg-white'
                        >
                          {row.unit && !row.libraryItem.unit_options.includes(row.unit) && (
                            <option value={row.unit}>{row.unit}</option>
                          )}
                          {row.libraryItem.unit_options.map(u => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type='text'
                          placeholder='°C…'
                          value={row.unit}
                          onChange={e => handleField(i, 'unit', e.target.value)}
                          className='text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 w-full'
                        />
                      )}

                      {/* Min value */}
                      <input
                        type='number'
                        placeholder='Min'
                        value={row.min_value}
                        onChange={e => handleField(i, 'min_value', e.target.value)}
                        className='text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 w-full'
                      />

                      {/* Max value */}
                      <input
                        type='number'
                        placeholder='Max'
                        value={row.max_value}
                        onChange={e => handleField(i, 'max_value', e.target.value)}
                        className='text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 w-full'
                      />

                      {/* Live value */}
                      <span
                        className={`text-[11px] font-mono text-right pr-1 ${
                          hasLive ? 'text-emerald-700 font-semibold' : 'text-gray-300'
                        }`}
                      >
                        {hasLive ? liveVal : '—'}
                      </span>
                    </div>
                  )
                })}
              </div>

              <p className='text-[10px] text-gray-400 mt-4'>
                Select a library entry — its name becomes the display label.
                Rows with no library selected are skipped on save.
              </p>
            </>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div className='px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2 flex-shrink-0'>
          <button
            onClick={onClose}
            className='text-xs px-4 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors'
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || isLoading || rows.length === 0}
            className='text-xs px-4 py-1.5 rounded-lg bg-blue-800 hover:bg-blue-900 text-white font-medium transition-colors disabled:opacity-50'
          >
            {isSaving ? 'Saving…' : 'Save Mappings'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ParamMappingModal
